import { FormControl } from '@mui/material';
import type { TimeInputProps } from '@schooly/style';
import { TimeInput } from '@schooly/style';
import { getControllerErrorText } from '@schooly/utils/get-controller-error-text';
import get from 'lodash.get';
import type { ControllerProps, FieldPath } from 'react-hook-form-lts';
import { Controller, useFormContext } from 'react-hook-form-lts';
import type { FieldValues } from 'react-hook-form-lts/dist/types';
import { useIntl } from 'react-intl';

export type ControlTimeInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> & TimeInputProps;

export const ControlTimeInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control: propsControl,
  ...props
}: ControlTimeInputProps<TFieldValues, TName>) => {
  const { $t } = useIntl();
  const { control: contextControl, formState } = useFormContext<TFieldValues>();
  const control = propsControl ?? contextControl;

  return (
    <Controller
      control={control}
      render={({ field }) => {
        const error = get(formState.errors, field.name);
        const hasError = props.error || Boolean(error);
        const errorMessage = getControllerErrorText<TFieldValues, TName>(error, props.rules, $t);

        return (
          <FormControl error={hasError} fullWidth={props.fullWidth}>
            <TimeInput
              {...props}
              {...props.rules}
              error={hasError}
              errorMessage={errorMessage}
              inputRef={field.ref}
              required={Boolean(props.rules?.required) || props.required}
              {...field}
            />
          </FormControl>
        );
      }}
      {...props}
    />
  );
};
